import React, { useEffect, useRef, useState } from "react";
import { Button, createStyles, Group, Popover } from "@mantine/core";
import { observer } from "mobx-react";
import { useImmerReducer } from "use-immer";
import { TreeNode, TreeNodeState, TreeProps } from "../models/tree-models";
import t from "../stores/language";
import theme from "../stores/theme";
import ModuleCreator, { ModuleCreateHandle } from "../components/module-creator";
import { Module } from "../models/models";
import { useStore } from "../stores/store";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { faArrowsAlt } from "@fortawesome/pro-duotone-svg-icons";
import { Icon } from "./icons";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";

const useStyles = createStyles((theme, { level }: { level: number }) => ({
  treeContainer: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  node: {
    paddingLeft: `${level * 10}px`,
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 2,
    ":hover": {
      button: {
        display: "block",
      },
      backgroundColor: theme.other.arc.colors[6],
    },
  },
  expandNode: {
    fontSize: 17,
    width: 20,
    marginRight: 6,
    marginLeft: 6,
    minWidth: 8,
    display: "flex",
    alignItems: "center",
    color: "black",
  },
  expandIcon: {
    cursor: "grab",
    color: "#fc7700",
    fontSize: "25px",
    marginLeft: "5px",
    opacity: 1,
  },
  contentArea: {
    display: "flex",
    cursor: "grab",
    alignItems: "center",
    width: "100%",
    padding: "0px 2px",
    overflow: "hidden",
    whiteSpace: "break-spaces",
  },
  nameContainer: {
    fontSize: 19,
    overflow: "hidden",
    textOverflow: "ellipsis",
    width: "100%",
    maxWidth: "230px",
  },
}));

type TreeState = {
  source: TreeNode[];
  data: TreeNodeState[];
  selectedValue: string | null;
  editableNode: string | null;
  expanded: string[];
};

type TreeAction =
  | { action: "ToggleExpand"; data: TreeNodeState }
  | { action: "Data"; data: TreeNode[] }
  | { action: "SelectValue"; data: string }
  | { action: "Restore"; data: { nodes: TreeNode[]; expanded: string[] | "all"; value: string } };

export const flatMapTree = (treeNodes: TreeNode[]): TreeNode[] => {
  let children: TreeNode[] = [];
  return treeNodes
    .map((node) => {
      const n = { ...node };
      if (n.children && n.children.length) {
        children = [...children, ...n.children];
      }
      return n;
    })
    .concat(children.length ? flatMapTree(children) : children);
};

const TreeReducer = (state: TreeState, action: TreeAction): TreeState => {
  const getNodeState = (node: TreeNode, level: number): TreeNodeState[] => {
    let nodeData: TreeNodeState[] = [];
    const parentNode: TreeNodeState = {
      name: node.name,
      id: node.id,
      selected: node.id == state.selectedValue,
      expanded: state.expanded.includes(node.id),
      level: level,
      editable: node.id == state.editableNode,
      children: node.children ?? [],
    };
    nodeData.push(parentNode);
    if (node.children && node.children.length > 0 && parentNode.expanded) {
      const children = node.children.map((childNode) => getNodeState(childNode, level + 1)).flat();
      nodeData = [parentNode, ...children];
    }
    return nodeData;
  };
  switch (action.action) {
    case "Data": {
      state.source = action.data;
      break;
    }
    case "ToggleExpand": {
      if (state.expanded.includes(action.data.id)) {
        state.expanded = state.expanded.filter((s) => s != action.data.id);
      } else {
        state.expanded.push(action.data.id);
      }
      break;
    }
    case "SelectValue": {
      state.selectedValue = action.data == state.selectedValue ? null : action.data;
      break;
    }
    case "Restore": {
      state.source = action.data.nodes;
      state.selectedValue = action.data.value;
      if (action.data.expanded != "all") {
        state.expanded = action.data.expanded;
      } else {
        state.expanded = action.data.nodes
          .map((node) => {
            const getRNodes = (cNode: TreeNode) => {
              let allChildren: string[] = [cNode.id];
              if (cNode.children != null && cNode.children.length > 0) {
                const cExpanded = [...new Set(cNode.children.map((cn) => getRNodes(cn)).flat())];
                allChildren = [...cExpanded, ...allChildren];
              }
              return allChildren;
            };
            return getRNodes(node);
          })
          .flat();
      }
      break;
    }
  }
  state.data = state.source.map((node) => getNodeState(node, 0)).flat();
  return state;
};

const defaultTreeState: TreeState = {
  source: [],
  data: [],
  selectedValue: "",
  editableNode: "",
  expanded: [],
};

export const Tree = observer(({ data, value, defaultExpandedNodes, onSelect, reOrder }: TreeProps) => {
  const [state, action] = useImmerReducer(TreeReducer, { ...defaultTreeState });
  const { classes } = useStyles({ level: 0 });
  const [activeId, setActiveId] = useState<string>("");

  const sensors = useSensors(
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    })
  );

  useEffect(() => {
    action({ action: "Restore", data: { nodes: data, value: value, expanded: defaultExpandedNodes ?? [] } });
  }, [JSON.stringify(data)]);

  useEffect(() => {
    action({ action: "SelectValue", data: value });
  }, []);

  const onSelectNode = (node: TreeNodeState) => {
    action({ action: "SelectValue", data: node.id });
    onSelect(node.id);
  };

  const onExpandToggle = (node: TreeNodeState) => {
    action({ action: "ToggleExpand", data: node });
  };

  const handleDragStart = (event: DragEndEvent) => {
    setActiveId(event.active.id.toString());
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (event.active == null || event.over == null || event.active.id === event.over.id) {
      return;
    }
    const activeModuleParent = state.data.find((d) => d.children.find((c) => c.id == event.active.id))?.id;
    const overModuleParent = state.data.find((d) => d.children.find((c) => c.id == event.over?.id))?.id;
    if (activeModuleParent != overModuleParent) {
      return;
    }
    if (event.active.id !== event.over.id) {
      const moduleIds = state.data.map((ts) => ts.id);
      const activeIndex = moduleIds.indexOf(event.active.id.toString());
      const overIndex = moduleIds.indexOf(event.over.id.toString());
      if (reOrder == null) return;
      const newOrder = arrayMove(moduleIds, activeIndex, overIndex).map((od) => parseInt(od));
      reOrder(newOrder);
    }
    setActiveId("");
  };

  return (
    <div className={classes.treeContainer}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      >
        <SortableContext items={state.data.map((od) => od.id)} strategy={verticalListSortingStrategy}>
          {state.data.map((node) => (
            <TreeNodeComponent
              node={node}
              key={node.id}
              onSelect={onSelectNode}
              onExpandToggle={onExpandToggle}
              isDragging={activeId ? true : false}
              isSelected={node.id == activeId}
            />
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
});

export const TreeNodeComponent = observer(
  ({
    node,
    onExpandToggle,
    onSelect,
    isDragging,
    isSelected,
  }: {
    node: TreeNodeState;
    onSelect: (node: TreeNodeState) => unknown;
    onExpandToggle: (node: TreeNodeState) => unknown;
    isDragging: boolean;
    isSelected: boolean;
  }) => {
    const { classes } = useStyles({ level: node.level });
    const moduleCreateRef = useRef<ModuleCreateHandle>(null);
    const [selectedModule, setModule] = useState<Module>();
    const [contextMenuOpen, setOpen] = useState<boolean>(false);
    const { store } = useStore();
    const params = useParams<{ moduleId: string; parent: string }>();
    const navigateTo = useNavigate();
    const { pathname } = useLocation();
    const selectedId = pathname.split("/")[pathname.split("/").length - 1];

    const { setNodeRef, attributes, transform, listeners, transition } = useSortable({ id: node.id });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    const expandToggle = () => {
      onExpandToggle(node);
    };
    const selectElement = () => {
      if (contextMenuOpen) {
        setOpen(false);
      }
      if (node.children.length > 0) return;
      onSelect(node);
    };
    const contextMenuClick = (e: { preventDefault: () => void }) => {
      if (params.parent || params.moduleId != null) return;
      e.preventDefault();
      const module = store.GetModuleById(node.id);
      setModule(module);
      setOpen(!contextMenuOpen);
    };
    const deleteModule = () => {
      if (selectedModule == null) {
        return;
      }
      store.DeleteModule({ ...selectedModule });
      if (node.selected) {
        navigateTo(`/main/formulaEditor/`);
      }
    };
    return (
      <div
        className={classes.node}
        ref={setNodeRef}
        style={{ ...style, backgroundColor: node.selected || node.id === selectedId ? theme.other?.arc.colors[1] : undefined }}
      >
        <div className={classes.expandNode}>
          {(node.children?.length ?? 0) > 0 ? (
            <div onClick={expandToggle} className={classes.expandIcon}>
              {node.expanded === true ? "-" : "+"}
            </div>
          ) : null}
        </div>
        <Popover zIndex={999} position={theme.other?.popOver.position} withArrow opened={contextMenuOpen} onClose={() => setOpen(false)}>
          <Popover.Target>
            <div className={classes.contentArea} onClick={selectElement} onContextMenu={contextMenuClick}>
              {params.parent || params.moduleId == null ? (
                <span
                  key={`${node.id}}|${-1}`}
                  {...attributes}
                  {...listeners}
                  style={{
                    cursor: isDragging && isSelected ? "grabbing" : "grab",
                    width: 30,
                  }}
                >
                  <Icon icon={faArrowsAlt} />
                </span>
              ) : null}
              <div className={classes.nameContainer}>{node.name}</div>
            </div>
          </Popover.Target>
          <Popover.Dropdown p={theme.other?.popOver.spacing["md"]}>
            <Group spacing={theme.other?.popOver.spacing["lg"]}>
              <Button
                size="xs"
                onClick={() => {
                  moduleCreateRef.current?.show(selectedModule ?? null);
                }}
              >
                {t("Edit")}
              </Button>
              <Button
                size="xs"
                color={theme.colors?.dangerColor ? theme.colors.dangerColor[0] : "red"}
                onClick={deleteModule}
                disabled={node.children.length > 0}
              >
                {t("Delete")}
              </Button>
            </Group>
          </Popover.Dropdown>
        </Popover>
        <ModuleCreator ref={moduleCreateRef}></ModuleCreator>
      </div>
    );
  }
);
